import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useExpedienteWebSocket } from '../hooks/useExpedienteWebSocket';
import apiClient from '../utils/api';

const ExpedienteLockContext = createContext();

export const ExpedienteLockProvider = ({ children, expedienteId, userId }) => {
  const [lockInfo, setLockInfo] = useState({
    isLocked: false,
    lockedBy: null,
    lockedByMe: false,
    lockTimestamp: null,
    userName: null
  });
  
  const [takeoverRequest, setTakeoverRequest] = useState(null);
  const [pendingTakeoverRequest, setPendingTakeoverRequest] = useState(null);
  const [isFirstUser, setIsFirstUser] = useState(false);
  const [hasCheckedInitialStatus, setHasCheckedInitialStatus] = useState(false);

  // Manejar cambios en el estado de bloqueo
  const handleLockChange = useCallback((data) => {
    console.log('[Context] ===== LOCK CHANGE =====');
    console.log('[Context] Data:', data);
    console.log('[Context] userName:', data.userName);
    console.log('[Context] userId:', data.userId);
    console.log('[Context] locked:', data.locked);
    console.log('[Context] Mi userId:', userId);
    
    const lockedByMe = data.userId === parseInt(userId);
    
    console.log('[Context] ¿Es mío el lock?', lockedByMe);
    
    setLockInfo({
      isLocked: data.locked || false,
      lockedBy: data.userId || null,
      lockedByMe: lockedByMe,
      lockTimestamp: data.locked ? Date.now() : null,
      userName: data.userName || null,
      expedienteId: data.expedienteId
    });

    // Solo marcar como primer usuario si YO obtuve el bloqueo Y está locked
    if (lockedByMe && data.locked) {
      console.log('[Context] ✅ Soy el primer usuario - tengo el lock');
      setIsFirstUser(true);
    } else if (!data.locked) {
      // Si nadie tiene bloqueo, resetear el estado
      console.log('[Context] Expediente desbloqueado');
      setIsFirstUser(false);
    } else {
      // Otro usuario tiene el bloqueo
      console.log('[Context] ❌ Otro usuario tiene el bloqueo');
      setIsFirstUser(false);
    }
  }, [userId]);

  // Manejar solicitudes de takeover recibidas
  const handleTakeoverRequest = useCallback((data) => {
    console.log('[Context] ===== TAKEOVER REQUEST RECEIVED =====');
    console.log('[Context] Data completa:', data);
    console.log('[Context] requesterName:', data.requesterName);
    console.log('[Context] requester:', data.requester);
    console.log('[Context] targetUserId:', data.targetUserId);
    console.log('[Context] Mi userId:', userId);
    
    // Verificar que el targetUserId coincide con mi userId
    // Si el mensaje llega aquí con mi userId, significa que tengo el bloqueo
    if (data.targetUserId && parseInt(data.targetUserId) === parseInt(userId)) {
      console.log('[Context] ✅ Takeover dirigido a mí - mostrando modal de solicitud');
      setTakeoverRequest({
        requestingUserId: data.requester,
        requestingUserName: data.requesterName,
        expedienteId: data.expedienteId,
        timestamp: Date.now()
      });
    } else {
      console.log('[Context] ❌ Takeover NO dirigido a mí');
      console.log('[Context] - targetUserId del mensaje:', data.targetUserId);
      console.log('[Context] - Mi userId:', userId);
    }
  }, [userId]);

  // Manejar respuestas de takeover
  const handleTakeoverResponse = useCallback((data) => {
    console.log('[Context] ===== TAKEOVER RESPONSE RECEIVED =====');
    console.log('[Context] Response data:', data);
    console.log('[Context] accepted:', data.accepted);
    console.log('[Context] requesterId:', data.requesterId);
    
    if (data.accepted) {
      console.log('[Context] ✅ Solicitud APROBADA - el backend transferirá el lock');
      // El backend ya hizo unlock + tryLock, confiar en el mensaje de lock-change
      // que vendrá por el topic. Solo actualizar estado local temporalmente.
      setLockInfo(prev => ({
        ...prev,
        lockedByMe: true,
        lockedBy: parseInt(userId),
        isLocked: true
      }));
      setIsFirstUser(true);
    } else {
      console.log('[Context] ❌ Solicitud RECHAZADA - permanezco en modo lectura');
    }
    
    // Limpiar solicitud pendiente en ambos casos
    setPendingTakeoverRequest(null);
    console.log('[Context] Solicitud pendiente limpiada');
  }, [userId]);

  // Hook de WebSocket
  const { 
    lockExpediente, 
    unlockExpediente, 
    requestTakeover: wsRequestTakeover,
    respondTakeover: wsRespondTakeover,
    isConnected,
    connectionError
  } = useExpedienteWebSocket({
    expedienteId,
    userId,
    onLockChange: handleLockChange,
    onTakeoverRequest: handleTakeoverRequest,
    onTakeoverResponse: handleTakeoverResponse
  });

  // Función para iniciar edición (bloquear expediente)
  const startEditing = useCallback(() => {
    // Solo bloquear si NO está bloqueado o si ya lo tengo yo
    console.log('startEditing called - isLocked:', lockInfo.isLocked, 'lockedByMe:', lockInfo.lockedByMe);
    
    if (lockInfo.isLocked && !lockInfo.lockedByMe) {
      console.log('No se puede bloquear - otro usuario tiene el control');
      return false;
    }
    
    if (lockInfo.lockedByMe) {
      console.log('Ya tengo el bloqueo - no enviar mensaje');
      return true;
    }
    
    console.log('Enviando solicitud de bloqueo');
    return lockExpediente();
  }, [lockInfo.isLocked, lockInfo.lockedByMe, lockExpediente]);

  // Función para finalizar edición (desbloquear expediente)
  const stopEditing = useCallback(() => {
    if (lockInfo.lockedByMe) {
      return unlockExpediente();
    }
    return false;
  }, [lockInfo.lockedByMe, unlockExpediente]);

  // Función para solicitar tomar control
  const requestTakeoverControl = useCallback(() => {
    if (lockInfo.isLocked && !lockInfo.lockedByMe && lockInfo.lockedBy) {
      setPendingTakeoverRequest({
        targetUserId: lockInfo.lockedBy,
        timestamp: new Date().toISOString()
      });
      return wsRequestTakeover(lockInfo.lockedBy);
    }
    return false;
  }, [lockInfo, wsRequestTakeover]);

  // Función para responder a solicitud de takeover
  const respondToTakeoverRequest = useCallback((approved) => {
    if (takeoverRequest) {
      console.log('[Context] Respondiendo a takeover - approved:', approved);
      const success = wsRespondTakeover(takeoverRequest.requestingUserId, approved);
      if (success) {
        setTakeoverRequest(null);
        if (approved) {
          console.log('[Context] Takeover ACEPTADO - el backend hará unlock/lock automáticamente');
          
          // 🔥 IMPORTANTE: NO hacer unlock manual aquí
          // El backend ya maneja unlock + lock en secuencia correcta
          // Solo actualizar estado local para reflejar que YA NO tengo el lock
          setLockInfo(prev => ({
            ...prev,
            lockedByMe: false,
            lockedBy: takeoverRequest.requestingUserId,
            userName: takeoverRequest.requestingUserName,
            isLocked: true
          }));
          setIsFirstUser(false);
          console.log('[Context] Estado local actualizado - esperando confirmación del backend');
        }
      }
      return success;
    }
    return false;
  }, [takeoverRequest, wsRespondTakeover]);

  // Función para rechazar solicitud de takeover
  const dismissTakeoverRequest = useCallback(() => {
    console.log('[Context] Cerrando modal/dismissing takeover');
    setTakeoverRequest(null);
    setPendingTakeoverRequest(null);
  }, []);

  // Consultar estado inicial del expediente y bloquear automáticamente si está libre
  useEffect(() => {
    const checkInitialStatusAndLock = async () => {
      // Solo ejecutar una vez cuando:
      // 1. WebSocket está conectado
      // 2. No se ha consultado el estado inicial aún
      // 3. Tenemos expedienteId y userId
      if (isConnected && !hasCheckedInitialStatus && expedienteId && userId) {
        console.log('[Context] Consultando estado inicial del expediente...');
        setHasCheckedInitialStatus(true);

        try {
          const response = await apiClient.get(`/api/expediente/${expedienteId}/lock-status`);
          const data = response.data;
          console.log('[Context] Estado inicial recibido:', data);

          if (data.locked) {
            // El expediente ya está bloqueado por alguien
            const lockedByMe = data.userId === parseInt(userId);
            console.log(`[Context] Expediente bloqueado por ${data.userName} (userId: ${data.userId})`);
            console.log(`[Context] ¿Soy yo? ${lockedByMe}`);
            
            setLockInfo({
              isLocked: true,
              lockedBy: data.userId,
              userName: data.userName,
              lockedByMe: lockedByMe,
              lockTimestamp: Date.now(),
              expedienteId: data.expedienteId
            });

            if (lockedByMe) {
              setIsFirstUser(true);
              console.log('[Context] Ya tengo el lock - no enviar solicitud duplicada');
            } else {
              console.log('[Context] Otro usuario tiene el lock - NO intentar robar');
              // NO llamar a lockExpediente() aquí - respetamos el lock existente
            }
          } else {
            // El expediente está libre - intentar bloquearlo (soy el primer usuario)
            console.log('[Context] Expediente libre - intentando bloqueo automático');
            lockExpediente();
          }
        } catch (error) {
          console.error('[Context] Error consultando estado inicial:', error);
          // 🔥 CAMBIO CRÍTICO: Si hay error, NO intentar bloquear automáticamente
          // porque podríamos estar robando el lock de otro usuario
          // En su lugar, simplemente fallar silenciosamente y esperar el WebSocket
          console.log('[Context] Error al consultar estado - esperando actualización por WebSocket');
        }
      }
    };

    checkInitialStatusAndLock();
  }, [isConnected, hasCheckedInitialStatus, expedienteId, userId, lockExpediente]);

  // Cleanup: liberar el bloqueo cuando el usuario sale del expediente (desmonta el Provider)
  useEffect(() => {
    return () => {
      console.log('[Context] ExpedienteLockProvider desmontándose');
      console.log('[Context] lockInfo.lockedByMe:', lockInfo.lockedByMe);
      
      // Solo hacer unlock si YO tengo el bloqueo
      if (lockInfo.lockedByMe) {
        console.log('[Context] Liberando bloqueo al salir del expediente');
        unlockExpediente();
      }
    };
  }, [lockInfo.lockedByMe, unlockExpediente]);

  const contextValue = {
    // Estado del bloqueo
    lockInfo,
    takeoverRequest,
    pendingTakeoverRequest,
    isFirstUser,
    
    // Estado de conexión
    isConnected,
    connectionError,
    
    // Acciones
    startEditing,
    stopEditing,
    requestTakeoverControl,
    respondToTakeoverRequest,
    dismissTakeoverRequest,
    
    // Funciones directas del WebSocket (para casos especiales)
    lockExpediente,
    unlockExpediente
  };

  return (
    <ExpedienteLockContext.Provider value={contextValue}>
      {children}
    </ExpedienteLockContext.Provider>
  );
};

export const useExpedienteLock = () => {
  const context = useContext(ExpedienteLockContext);
  if (!context) {
    throw new Error('useExpedienteLock must be used within an ExpedienteLockProvider');
  }
  return context;
};

export default ExpedienteLockContext;